import { useState, useEffect } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { OtherPlumbers } from '../../components/directory/OtherPlumbers'
import {
  Star, MapPin, Shield, Clock, Wrench,
  Zap, BadgePoundSterling, ChevronDown, ChevronUp, Phone,
  Camera, Award, Building2, Home, Receipt, Navigation, ChevronLeft, ChevronRight, X,
} from 'lucide-react'
import { getDirectoryPlumber } from '../../lib/api'
import { SUPPORT_PHONE_DISPLAY, SHOW_SUPPORT_PHONE } from '../../lib/contact'
import { TextUsLink } from '../../components/TextUsLink'
import { useDirectoryMode } from '../../hooks/useDirectoryMode'
import { formatPlumberBaseLocation } from '../../lib/plumberLocation'
import { DIRECTORY_MAIN_GRID, DIRECTORY_PAGE_MAX, DIRECTORY_SIDEBAR_COL, slugToLabel } from './directoryUtils'
import { PageWrapper } from '../../components/layout/PageWrapper'
import { Breadcrumb } from '../../components/Breadcrumb'
import { Seo } from '../../components/Seo'
import { DirectoryPlumberSkeleton } from '../../components/ui/Skeletons'
import { CertBadge } from '../../components/ui/CertBadge'
import { MobileBookBar } from '../../components/MobileBookBar'
import { logStatsigEvent } from '../../lib/statsig'
import { goToEmergencyForm } from '../../lib/goToEmergencyForm'
import { googleMapEmbedUrl, MAP_EMBED_REFERRER_POLICY } from '../../lib/mapEmbed'

// ── About text processing ──────────────────────────────────────────────────────

const CONTACT_PATTERNS = [
  /\b(?:call|phone|ring|tel(?:ephone)?)\s+(?:us|me|now|today)?\s*(?:on|at)?[\s:]*(?:\+?[\d\s\-().]{7,})/i,
  /[\w.+%-]+@[\w.-]+\.[a-z]{2,}/i,
  /(?:https?:\/\/|www\.)\S+/i,
  /\b(?:visit\s+(?:us|our\s+(?:website|site))|find\s+us\s+(?:at|on)|contact\s+us|get\s+in\s+touch|call\s+us|email\s+us|reach\s+us)\b/i,
]

type Segment = { type: 'prose'; text: string } | { type: 'bullets'; items: string[] }

function parseAboutText(raw: string): Segment[] {
  const lines = raw.split(/\r?\n/).map(l => l.trim()).filter(Boolean)

  const segments: Segment[] = []
  let bulletBuffer: string[] = []

  const flushBullets = () => {
    if (bulletBuffer.length) { segments.push({ type: 'bullets', items: bulletBuffer }); bulletBuffer = [] }
  }

  for (const line of lines) {
    // Strip contact info lines entirely
    if (CONTACT_PATTERNS.some(p => p.test(line))) continue

    const bulletMatch = line.match(/^(?:[*•\-]\s+|(\d+)[.)]\s+)(.+)/)
    if (bulletMatch) {
      bulletBuffer.push(bulletMatch[2] ?? bulletMatch[0])
      continue
    }

    flushBullets()

    // Split prose line into sentences, filter contact-heavy sentences
    const sentences = line.match(/[^.!?]+[.!?]*/g) ?? [line]
    const clean = sentences
      .map(s => s.trim())
      .filter(s => s.length > 1 && !CONTACT_PATTERNS.some(p => p.test(s)))
      .join(' ')
      .trim()

    if (clean) segments.push({ type: 'prose', text: clean })
  }

  flushBullets()
  return segments
}

function segmentsCharCount(segs: Segment[]): number {
  return segs.reduce((n, s) => n + (s.type === 'prose' ? s.text.length : s.items.join(' ').length), 0)
}

const ABOUT_COLLAPSE_CHARS = 320

function AboutSection({ text, foundedYear }: { text: string; foundedYear?: number }) {
  const [expanded, setExpanded] = useState(false)
  const all = parseAboutText(text)
  if (!all.length && !foundedYear) return null

  // Determine collapsed slice: accumulate segments until we exceed the threshold,
  // always stopping at a segment boundary (never mid-sentence/mid-list).
  let charCount = 0
  let cutAt = all.length
  for (let i = 0; i < all.length; i++) {
    const segLen = segmentsCharCount([all[i]!])
    if (charCount + segLen > ABOUT_COLLAPSE_CHARS && i > 0) { cutAt = i; break }
    charCount += segLen
  }
  const needsToggle = cutAt < all.length
  const visible = expanded || !needsToggle ? all : all.slice(0, cutAt)

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">About</h2>
        <span className="text-[10px] font-medium text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">AI summary</span>
      </div>
      <div className="space-y-3 text-gray-700 leading-relaxed text-sm">
        {visible.map((seg, i) =>
          seg.type === 'bullets' ? (
            <ul key={i} className="list-disc list-inside space-y-1 text-gray-700">
              {seg.items.map((item, j) => <li key={j}>{item}</li>)}
            </ul>
          ) : (
            <p key={i}>{seg.text}</p>
          )
        )}
      </div>
      {needsToggle && (
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="mt-3 flex items-center gap-1 text-sm text-trust-500 hover:text-trust-700 transition-colors"
        >
          {expanded
            ? <><ChevronUp className="h-4 w-4" /> Show less</>
            : <><ChevronDown className="h-4 w-4" /> Show more</>}
        </button>
      )}
      {foundedYear && (
        <p className="text-xs text-gray-400 mt-3">Established {foundedYear}</p>
      )}
    </div>
  )
}


const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const DEFAULT_BUSINESS_HOURS = [
  { day: 1, opensAt: '09:00', closesAt: '17:00' },
  { day: 2, opensAt: '09:00', closesAt: '17:00' },
  { day: 3, opensAt: '09:00', closesAt: '17:00' },
  { day: 4, opensAt: '09:00', closesAt: '17:00' },
  { day: 5, opensAt: '09:00', closesAt: '17:00' },
]

export function DirectoryPlumber() {
  const { countrySlug, countySlug, townSlug, postcodeSlug, directorySlug } = useParams<{
    countrySlug: string
    countySlug: string
    townSlug: string
    postcodeSlug: string
    directorySlug: string
  }>()

  const { isEmergency, isGasSafe, isWaterSafe, basePath } = useDirectoryMode()
  const navigate = useNavigate()

  const { data: plumber, isLoading } = useQuery({
    queryKey: ['directory', 'plumber', countrySlug, countySlug, townSlug, postcodeSlug, directorySlug, isEmergency, isGasSafe, isWaterSafe],
    queryFn: () => getDirectoryPlumber(countrySlug!, countySlug!, townSlug!, postcodeSlug!, directorySlug!, isEmergency, isGasSafe, isWaterSafe),
    enabled: !!(countrySlug && countySlug && townSlug && postcodeSlug && directorySlug),
  })

  const [showAllReviews, setShowAllReviews] = useState(false)
  const [showAllPhotos, setShowAllPhotos] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const [showAllCoverage, setShowAllCoverage] = useState(false)

  const isPlaceholder = (slug?: string) => !slug || slug === 'unknown'
  const countryLabel = isPlaceholder(countrySlug) ? '' : slugToLabel(countrySlug)
  const countyLabel = isPlaceholder(countySlug) ? '' : slugToLabel(countySlug)
  const townLabel = isPlaceholder(townSlug) ? '' : slugToLabel(townSlug)
  const displayPostcode = isPlaceholder(postcodeSlug) ? '' : (postcodeSlug?.toUpperCase() ?? '')

  // Flat ordered list of all photo URLs for the lightbox carousel (work photos first, then review photos)
  const allLightboxPhotos: string[] = [
    ...(plumber?.photos ?? []).map((p: { url: string }) => p.url),
    ...(plumber?.reviews ?? []).flatMap((r: { photos?: string[] }) => r.photos ?? []),
  ].filter(Boolean)

  // Autoplay: advance every 4 s; resets whenever the user manually navigates
  useEffect(() => {
    if (lightboxIndex === null || allLightboxPhotos.length <= 1) return
    const total = allLightboxPhotos.length
    const t = setInterval(() => setLightboxIndex(i => i === null ? null : (i + 1) % total), 4000)
    return () => clearInterval(t)
  }, [lightboxIndex, allLightboxPhotos.length])

  // Top of the profile→booking funnel (see lib/statsig.ts).
  const plumberIdForView = plumber?._id
  useEffect(() => {
    if (!plumberIdForView) return
    logStatsigEvent('plumber_profile_viewed', plumberIdForView, { page: 'directory-profile' })
  }, [plumberIdForView])

  if (isLoading) {
    return <PageWrapper hideCallBar><DirectoryPlumberSkeleton /></PageWrapper>
  }

  if (!plumber) {
    return (
      <PageWrapper>
        <div className="max-w-xl mx-auto px-4 py-16 text-center">
          <p className="text-gray-500">Plumber not found.</p>
          <Link to={`${basePath}/${countrySlug}/${countySlug}/${townSlug}/${postcodeSlug}`} className="text-trust-500 hover:underline text-sm mt-2 inline-block">
            Back to {displayPostcode}
          </Link>
        </div>
      </PageWrapper>
    )
  }

  const platformRatings = plumber.platformRatings ?? []
  const certifications = plumber.certifications ?? []
  const qualifications = plumber.qualifications ?? []
  const trades = plumber.trades ?? []
  const specialties = plumber.specialties ?? []
  const schedule = plumber.workingHours?.schedule ?? []
  const usingAssumedHours = !plumber.workingHours?.alwaysOpen && schedule.length === 0
  const displaySchedule = usingAssumedHours ? DEFAULT_BUSINESS_HOURS : schedule

  const googleRating = plumber.googleRating
    ?? platformRatings.find(p => p.platform === 'google')?.rating
  const googleReviewCount = plumber.googleReviewCount
    ?? platformRatings.find(p => p.platform === 'google')?.reviewCount

  const baseLocation = formatPlumberBaseLocation(plumber)
  const coords = plumber.coordinates
  const mapSrc = googleMapEmbedUrl((coords && coords.lat !== 0 && coords.lng !== 0)
    ? coords
    : { query: [plumber.postcode, 'UK'].filter(Boolean).join(', ') })

  const metaDescription = plumber.description
    ? plumber.description.slice(0, 155)
    : baseLocation
      ? `${plumber.companyName} - ${plumber.emergencyAvailable ? 'Emergency plumber' : 'Plumber'} based in ${baseLocation}. ${plumber.emergencyAvailable ? 'Available 24/7 for urgent plumbing work.' : 'Qualified local plumber.'}`
      : `${plumber.companyName} - ${plumber.emergencyAvailable ? 'Emergency plumber' : 'Qualified local plumber'}. ${plumber.emergencyAvailable ? 'Available 24/7 for urgent plumbing work.' : 'Contact for local plumbing work.'}`

  const localBusinessLd = {
    '@context': 'https://schema.org',
    '@type': 'Plumber',
    name: plumber.companyName,
    description: plumber.description ?? undefined,
    ...(plumber.logoUrl && { image: plumber.logoUrl }),
    address: {
      '@type': 'PostalAddress',
      ...(plumber.city?.trim() && { addressLocality: plumber.city.trim() }),
      ...(plumber.region?.trim() && { addressRegion: plumber.region.trim() }),
      ...(plumber.postcode?.trim() && { postalCode: plumber.postcode.trim() }),
      addressCountry: 'GB',
    },
    // No telephone here on purpose: this object IS the plumber, so the
    // property would assert our number is theirs. See profileJsonLd in
    // server/src/routes/ssrMeta.ts, and commit 404a96d, for why.
    ...(googleRating !== undefined && googleReviewCount !== undefined && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: googleRating,
        reviewCount: googleReviewCount,
        bestRating: 5,
        worstRating: 1,
      },
    }),
    ...(plumber.workingHours?.alwaysOpen && {
      openingHours: 'Mo-Su 00:00-24:00',
    }),
  }

  return (
    <PageWrapper
      hideCallBar
      bottomBar={
        <MobileBookBar
          plumberId={plumber._id}
          companyName={plumber.companyName}
          rating={googleRating}
          reviewCount={googleReviewCount}
          emergencyAvailable={plumber.emergencyAvailable}
          page="directory-profile"
        />
      }
    >
      <Seo
        title={`${plumber.companyName}${baseLocation ? ` - Plumber in ${baseLocation}` : ' - Plumber'} | FixDelivered`}
        description={metaDescription}
        path={`${basePath}/${countrySlug}/${countySlug}/${townSlug}/${postcodeSlug}/${directorySlug}`}
        breadcrumbs={[
          { name: 'UK', url: `${basePath}` },
          ...(countryLabel ? [{ name: countryLabel, url: `${basePath}/${countrySlug}` }] : []),
          ...(countyLabel ? [{ name: countyLabel, url: `${basePath}/${countrySlug}/${countySlug}` }] : []),
          ...(townLabel && !countyLabel.toLowerCase().endsWith(townLabel.toLowerCase()) ? [{ name: townLabel, url: `${basePath}/${countrySlug}/${countySlug}/${townSlug}` }] : []),
          ...(displayPostcode ? [{ name: displayPostcode, url: `${basePath}/${countrySlug}/${countySlug}/${townSlug}/${postcodeSlug}` }] : []),
          { name: plumber.companyName, url: `${basePath}/${countrySlug}/${countySlug}/${townSlug}/${postcodeSlug}/${directorySlug}` },
        ]}
        structuredData={localBusinessLd}
      />

      <div className={DIRECTORY_PAGE_MAX}>
        <div className={DIRECTORY_MAIN_GRID}>
          <div className="min-w-0">
            <Breadcrumb items={[
              ...(countryLabel ? [{ label: countryLabel, href: `${basePath}/${countrySlug}` }] : []),
              ...(countyLabel ? [{ label: countyLabel, href: `${basePath}/${countrySlug}/${countySlug}` }] : []),
              ...(townLabel && !countyLabel.toLowerCase().endsWith(townLabel.toLowerCase()) ? [{ label: townLabel, href: `${basePath}/${countrySlug}/${countySlug}/${townSlug}` }] : []),
              ...(displayPostcode ? [{ label: displayPostcode, href: `${basePath}/${countrySlug}/${countySlug}/${townSlug}/${postcodeSlug}` }] : []),
              { label: plumber.companyName },
            ]} />

            <div className="mt-6 space-y-5">

            {/* ── Header ────────────────────────────────────────────────────── */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0 w-16 h-16 rounded-xl bg-white border border-gray-200 overflow-hidden flex items-center justify-center">
                  {/* Logo if we have one, else fall back to a work photo, else initial. */}
                  {plumber.logoUrl || plumber.photos?.[0]?.url ? (
                    <img
                      src={plumber.logoUrl || plumber.photos![0].url}
                      alt={plumber.companyName}
                      width={64}
                      height={64}
                      className={`w-full h-full ${plumber.logoUrl ? 'object-contain' : 'object-cover'}`}
                      onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
                    />
                  ) : (
                    <span className="text-2xl font-bold text-gold-500">
                      {plumber.companyName.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  {plumber.isTestDummy && (
                    <div className="flex items-center gap-2 bg-yellow-50 border border-yellow-300 rounded-lg px-3 py-2 mb-3">
                      <span className="text-yellow-700 text-xs font-bold uppercase tracking-wide">⚠ TEST DUMMY - Local Only</span>
                      <span className="text-yellow-600 text-xs">This profile is for local testing and is not visible in production.</span>
                    </div>
                  )}
                  {plumber.emergencyAvailable && (
                    <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-emergency-600 bg-emergency-50 border border-emergency-100 px-2 py-0.5 rounded-full mb-2">
                      <Zap className="h-3 w-3" /> 24/7 Emergency
                    </span>
                  )}
                  <h1 className="text-2xl font-bold text-gray-900">{plumber.companyName}</h1>
                  {plumber.tagline && <p className="text-gray-500 text-sm mt-0.5 italic">{plumber.tagline}</p>}
                  <div className="flex items-center gap-1.5 text-sm text-gray-400 mt-2">
                    <MapPin className="h-4 w-4 flex-shrink-0" />
                    {baseLocation || '-'}
                  </div>
                </div>
              </div>

              {googleRating !== undefined && googleRating > 0 && (
                <div className="flex items-center gap-2 mt-4">
                  <div className="flex">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} className={`h-4 w-4 ${i <= Math.round(googleRating) ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'}`} />
                    ))}
                  </div>
                  <span className="text-sm font-medium text-gray-700">{googleRating.toFixed(1)}</span>
                  {googleReviewCount !== undefined && (
                    <span className="text-sm text-gray-400">· {googleReviewCount.toLocaleString()} reviews</span>
                  )}
                </div>
              )}



              {/* Company type badges */}
              {(plumber.doesDomestic || plumber.doesCommercial || plumber.vatRegistered) && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {plumber.doesDomestic && (
                    <span className="inline-flex items-center gap-1 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100 px-2.5 py-1 rounded-full">
                      <Home className="h-3 w-3" /> Domestic
                    </span>
                  )}
                  {plumber.doesCommercial && (
                    <span className="inline-flex items-center gap-1 text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100 px-2.5 py-1 rounded-full">
                      <Building2 className="h-3 w-3" /> Commercial
                    </span>
                  )}
                  {plumber.vatRegistered && (
                    <span className="inline-flex items-center gap-1 text-xs font-medium bg-gray-50 text-gray-600 border border-gray-200 px-2.5 py-1 rounded-full">
                      <Receipt className="h-3 w-3" /> VAT Registered{plumber.vatNumber ? ` · ${plumber.vatNumber}` : ''}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* ── About ─────────────────────────────────────────────────────── */}
            {(plumber.description || plumber.bio) && (
              <AboutSection
                text={plumber.description || plumber.bio!}
                foundedYear={plumber.foundedYear}
              />
            )}

            {/* ── Work Photos ────────────────────────────────────────────────── */}
            {plumber.photos && plumber.photos.length > 0 && (() => {
              const visiblePhotos = showAllPhotos ? plumber.photos : plumber.photos.slice(0, 6)
              return (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-2">
                      <Camera className="h-4 w-4" /> Work Photos
                    </h2>
                    <span className="text-xs text-gray-400">{plumber.photos.length} photos</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {visiblePhotos.map((photo, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setLightboxIndex(allLightboxPhotos.indexOf(photo.url))}
                        className="aspect-square rounded-lg overflow-hidden bg-gray-100 hover:opacity-90 transition-opacity focus:outline-none"
                      >
                        <img
                          src={photo.url}
                          alt={photo.caption ?? `${plumber.companyName} plumbing work${plumber.city ? ` in ${plumber.city}` : ''} — photo ${i + 1}`}
                          loading="lazy"
                          className="w-full h-full object-cover"
                          onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
                        />
                      </button>
                    ))}
                  </div>
                  {plumber.photos.length > 6 && (
                    <button
                      type="button"
                      onClick={() => setShowAllPhotos(v => !v)}
                      className="mt-3 text-sm text-trust-500 hover:text-trust-700 transition-colors"
                    >
                      {showAllPhotos ? 'Show fewer' : `See all ${plumber.photos.length} photos`}
                    </button>
                  )}
                </div>
              )
            })()}

            {/* ── Lightbox carousel ───────────────────────────────────────────── */}
            {lightboxIndex !== null && (() => {
              const total = allLightboxPhotos.length
              const idx = lightboxIndex
              const prevIdx = (idx - 1 + total) % total
              const nextIdx = (idx + 1) % total
              const close = () => setLightboxIndex(null)
              const go = (i: number) => setLightboxIndex(i)

              return (
                <div
                  className="fixed inset-0 z-50 bg-black/95 flex flex-col h-full select-none"
                  onKeyDown={e => {
                    if (e.key === 'Escape') close()
                    if (e.key === 'ArrowLeft') go(prevIdx)
                    if (e.key === 'ArrowRight') go(nextIdx)
                  }}
                  tabIndex={-1}
                  ref={el => el?.focus()}
                  onClick={close}
                >
                  {/* Header */}
                  <div className="flex items-center justify-between px-5 py-4 flex-shrink-0" onClick={e => e.stopPropagation()}>
                    <span className="text-white/40 text-sm tabular-nums">{idx + 1} / {total}</span>
                    <button
                      type="button"
                      onClick={close}
                      className="p-2 rounded-xl text-white/60 hover:text-white hover:bg-white/10 transition-colors"
                      aria-label="Close"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Stage - clicking the black area closes; interactive children stop propagation */}
                  <div className="flex-1 min-h-0 relative flex items-center overflow-hidden">
                    {/* Prev - faded peek on left */}
                    {total > 1 && (
                      <button
                        type="button"
                        onClick={e => { e.stopPropagation(); go(prevIdx) }}
                        className="absolute left-0 top-0 h-full w-[13%] flex items-center justify-end pr-2 z-10 cursor-pointer"
                        aria-label="Previous photo"
                      >
                        <img
                          src={allLightboxPhotos[prevIdx]}
                          alt=""
                          className="h-4/5 w-full object-cover rounded-r-xl opacity-25 hover:opacity-40 transition-opacity"
                        />
                      </button>
                    )}

                    {/* Current photo */}
                    <div className="flex-1 flex items-center justify-center px-[14%] h-full" onClick={e => e.stopPropagation()}>
                      <img
                        src={allLightboxPhotos[idx]}
                        alt={`Photo ${idx + 1}`}
                        className="max-w-full max-h-full object-contain rounded-xl shadow-2xl"
                      />
                    </div>

                    {/* Next - faded peek on right */}
                    {total > 1 && (
                      <button
                        type="button"
                        onClick={e => { e.stopPropagation(); go(nextIdx) }}
                        className="absolute right-0 top-0 h-full w-[13%] flex items-center justify-start pl-2 z-10 cursor-pointer"
                        aria-label="Next photo"
                      >
                        <img
                          src={allLightboxPhotos[nextIdx]}
                          alt=""
                          className="h-4/5 w-full object-cover rounded-l-xl opacity-25 hover:opacity-40 transition-opacity"
                        />
                      </button>
                    )}

                    {/* Arrow buttons */}
                    {total > 1 && (
                      <>
                        <button
                          type="button"
                          onClick={e => { e.stopPropagation(); go(prevIdx) }}
                          className="absolute left-[14%] p-2.5 rounded-full bg-black/40 hover:bg-black/70 text-white transition-colors z-20"
                          aria-label="Previous"
                        >
                          <ChevronLeft className="h-5 w-5" />
                        </button>
                        <button
                          type="button"
                          onClick={e => { e.stopPropagation(); go(nextIdx) }}
                          className="absolute right-[14%] p-2.5 rounded-full bg-black/40 hover:bg-black/70 text-white transition-colors z-20"
                          aria-label="Next"
                        >
                          <ChevronRight className="h-5 w-5" />
                        </button>
                      </>
                    )}
                  </div>

                  {/* Dot indicators */}
                  {total > 1 && total <= 20 && (
                    <div className="flex items-center justify-center gap-1.5 py-4 flex-shrink-0" onClick={e => e.stopPropagation()}>
                      {allLightboxPhotos.map((_, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => go(i)}
                          className={`rounded-full transition-all ${i === idx ? 'w-4 h-1.5 bg-white' : 'w-1.5 h-1.5 bg-white/30 hover:bg-white/60'}`}
                          aria-label={`Photo ${i + 1}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )
            })()}

            {/* ── Quality scores ────────────────────────────────────────────── */}
            {plumber.qualityScores && plumber.qualityScores.overall !== undefined && (() => {
              const qs = plumber.qualityScores!
              const scoreBar = (val: number) => (
                <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-trust-500 rounded-full"
                    style={{ width: `${(val / 5) * 100}%` }}
                  />
                </div>
              )
              return (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Quality scores</h2>
                    <div className="flex items-center gap-1">
                      <Star className="h-4 w-4 text-amber-400 fill-amber-400" />
                      <span className="text-lg font-bold text-gray-900">{qs.overall!.toFixed(1)}</span>
                      <span className="text-xs text-gray-400 ml-0.5">/ 5</span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {qs.quality !== undefined && (
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-500 w-28 flex-shrink-0">Quality of work</span>
                        {scoreBar(qs.quality)}
                        <span className="text-xs font-medium text-gray-700 w-7 text-right">{qs.quality.toFixed(1)}</span>
                      </div>
                    )}
                    {qs.reliability !== undefined && (
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-500 w-28 flex-shrink-0">Reliability</span>
                        {scoreBar(qs.reliability)}
                        <span className="text-xs font-medium text-gray-700 w-7 text-right">{qs.reliability.toFixed(1)}</span>
                      </div>
                    )}
                    {qs.communication !== undefined && (
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-500 w-28 flex-shrink-0">Communication</span>
                        {scoreBar(qs.communication)}
                        <span className="text-xs font-medium text-gray-700 w-7 text-right">{qs.communication.toFixed(1)}</span>
                      </div>
                    )}
                    {qs.availability !== undefined && (
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-500 w-28 flex-shrink-0">Availability</span>
                        {scoreBar(qs.availability)}
                        <span className="text-xs font-medium text-gray-700 w-7 text-right">{qs.availability.toFixed(1)}</span>
                      </div>
                    )}
                  </div>
                  {qs.reviewCount !== undefined && (
                    <p className="text-xs text-gray-400 mt-3">Based on {qs.reviewCount} reviewed job{qs.reviewCount !== 1 ? 's' : ''}</p>
                  )}
                </div>
              )
            })()}

            {/* ── Qualifications & certifications ────────────────────────────── */}
            {(certifications.length > 0 || qualifications.length > 0 || plumber.gasSafeNumber) && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Qualifications & certifications</h2>
                <div className="flex flex-wrap gap-2">
                  {certifications.map(c => (
                    <CertBadge
                      key={c}
                      name={c}
                      suffix={c === 'Gas Safe Registered' && plumber.gasSafeNumber ? `#${plumber.gasSafeNumber}` : undefined}
                    />
                  ))}
                  {qualifications.map(q => (
                    <CertBadge key={q} name={q} />
                  ))}
                </div>
              </div>
            )}

            {/* ── Awards ────────────────────────────────────────────────────── */}
            {plumber.awards && plumber.awards.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4 flex items-center gap-2">
                  <Award className="h-4 w-4" /> Awards & recognition
                </h2>
                <div className="flex flex-wrap gap-3">
                  {plumber.awards.map((award, i) => (
                    <div key={i} className="flex items-center gap-2 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
                      {award.badgeUrl ? (
                        <img src={award.badgeUrl} alt={award.name} className="h-8 w-8 object-contain" onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none' }} />
                      ) : (
                        <Award className="h-5 w-5 text-amber-500 flex-shrink-0" />
                      )}
                      <div>
                        <p className="text-xs font-semibold text-amber-800">{award.name}</p>
                        {award.year && <p className="text-xs text-amber-600">{award.year}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Popular services ──────────────────────────────────────────── */}
            {(specialties.length > 0 || trades.length > 0) && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <div className="flex items-start justify-between gap-3 mb-1">
                  <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Popular services</h2>
                </div>
                <p className="text-xs text-gray-400 mb-4">Most commonly offered based on this profile. Call to confirm availability.</p>
                <div className="flex flex-wrap gap-2">
                  {specialties.map(s => (
                    <span key={s} className="inline-flex items-center gap-1 text-xs font-medium bg-gray-100 text-gray-700 px-2.5 py-1.5 rounded-full">
                      <Wrench className="h-3 w-3 text-gray-400" />{s}
                    </span>
                  ))}
                  {trades.filter(t => !specialties.includes(t)).map(t => (
                    <span key={t} className="inline-flex items-center gap-1 text-xs font-medium bg-gray-100 text-gray-600 px-2.5 py-1.5 rounded-full capitalize">
                      <Wrench className="h-3 w-3 text-gray-400" />{t}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-3 flex items-center gap-1">
                  <Phone className="h-3 w-3" />
                  Not seeing what you need? Contact them directly to discuss your job.
                </p>
              </div>
            )}


            {/* ── Working hours ─────────────────────────────────────────────── */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Availability</h2>

              {plumber.emergencyAvailable && (
                <div className="flex items-center gap-2.5 bg-emergency-50 border border-emergency-100 rounded-xl px-4 py-3 mb-4">
                  <Zap className="h-4 w-4 text-emergency-600 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-emergency-700">Available 24/7 for emergencies</p>
                    <p className="text-xs text-emergency-600/70 mt-0.5">This plumber accepts urgent callouts nights, weekends and bank holidays.</p>
                  </div>
                </div>
              )}

              {plumber.workingHours?.alwaysOpen ? (
                <div className="flex items-center gap-2 text-sm font-medium text-emerald-700">
                  <Clock className="h-4 w-4" />Open 24 hours, 7 days a week
                </div>
              ) : (
                <div className="space-y-1">
                  {displaySchedule.map(slot => (
                    <div key={slot.day} className="flex items-center justify-between text-sm text-gray-600 py-1.5 border-b border-gray-50 last:border-0">
                      <span className="font-medium w-10">{DAY_NAMES[slot.day]}</span>
                      <span>{slot.opensAt} – {slot.closesAt}</span>
                    </div>
                  ))}
                  {usingAssumedHours && (
                    <p className="text-xs text-gray-400 pt-2">No hours were set on this profile, so standard hours are shown.</p>
                  )}
                </div>
              )}
            </div>

            {/* ── Reviews ───────────────────────────────────────────────────── */}
            {plumber.reviews && plumber.reviews.length > 0 && (() => {
              const sorted = [...plumber.reviews].sort((a, b) => {
                const dateA = a.publishedAt ? new Date(a.publishedAt).getTime() : 0
                const dateB = b.publishedAt ? new Date(b.publishedAt).getTime() : 0
                return dateB - dateA
              })
              const visible = showAllReviews ? sorted : sorted.slice(0, 3)
              const hidden = sorted.length - 3
              return (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                  <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
                    Reviews ({plumber.reviews.length})
                  </h2>
                  <div className="space-y-4">
                    {visible.map((r, i) => (
                      <div key={i} className="border-b border-gray-50 pb-4 last:border-0 last:pb-0">
                        <div className="flex items-start justify-between gap-2">
                          <span className="text-sm font-medium text-gray-800">{r.authorName ?? 'Anonymous'}</span>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {r.rating !== undefined && r.rating > 0 && (
                              <div className="flex">
                                {[1,2,3,4,5].map(j => (
                                  <Star key={j} className={`h-3 w-3 ${j <= Math.round(r.rating!) ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'}`} />
                                ))}
                              </div>
                            )}
                            {r.relativeTime && <span className="text-xs text-gray-400 ml-1">{r.relativeTime}</span>}
                          </div>
                        </div>
                        {r.text && <p className="text-sm text-gray-600 mt-1.5 leading-relaxed">{r.text}</p>}
                        {r.photos && r.photos.length > 0 && (
                          <div className="flex gap-2 mt-2 flex-wrap">
                            {r.photos.map((photoUrl, pi) => (
                              <button
                                key={pi}
                                type="button"
                                onClick={() => setLightboxIndex(allLightboxPhotos.indexOf(photoUrl))}
                                className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 hover:opacity-90 transition-opacity"
                              >
                                <img src={photoUrl} alt={`Review photo ${pi + 1}`} loading="lazy" className="w-full h-full object-cover" />
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {sorted.length > 3 && (
                    <button
                      type="button"
                      onClick={() => setShowAllReviews(v => !v)}
                      className="mt-4 flex items-center gap-1 text-sm text-trust-500 hover:text-trust-700 transition-colors"
                    >
                      {showAllReviews ? (
                        <><ChevronUp className="h-4 w-4" /> Show fewer reviews</>
                      ) : (
                        <><ChevronDown className="h-4 w-4" /> See {hidden} more review{hidden !== 1 ? 's' : ''}</>
                      )}
                    </button>
                  )}
                </div>
              )
            })()}

            {/* ── Map ───────────────────────────────────────────────────────── */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-6 pt-5 pb-3">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Headquarters</h2>
                <div className="flex items-center gap-1.5 mt-1">
                  <MapPin className="h-4 w-4 text-trust-500 flex-shrink-0" />
                  <p className="text-sm font-medium text-gray-800">{baseLocation || '-'}</p>
                </div>
              </div>
              <div className="relative">
                <iframe
                  title={`Location of ${plumber.companyName}`}
                  src={mapSrc}
                  width="100%"
                  height="260"
                  style={{ border: 0, display: 'block' }}
                  loading="lazy"
                  referrerPolicy={MAP_EMBED_REFERRER_POLICY}
                />
              </div>
            </div>

            {/* ── Emergency CTA ─────────────────────────────────────────────── */}
            <div className="bg-[#0f172a] rounded-2xl p-6 text-center text-white">
              <p className="text-sm text-gray-300 mb-3">Need a plumber right now?</p>
              {/* Restored (issue #78) to the shape 404a96d removed: calling is
                  the primary action again, the online route is secondary.
                  Rupert pulled this in Aug 2026 because it read as the
                  plumber's own number and callers rang us expecting them; it
                  is back by explicit decision now that the line takes a
                  voicemail and answers texts (issue #79) rather than playing
                  a "we can't take calls" message. Worth knowing that voicemail
                  does not by itself fix the misattribution he described. */}
              {SHOW_SUPPORT_PHONE && (
                <TextUsLink
                  onClick={() => logStatsigEvent('plumber_text_cta_clicked', plumber._id, { placement: 'cta-card', page: 'directory-profile' })}
                  className="flex items-center justify-center gap-2 bg-emergency-600 hover:bg-emergency-700 text-white font-bold px-6 py-3.5 rounded-xl transition-colors text-sm"
                >
                  <Phone className="h-4 w-4" /> Text us now · {SUPPORT_PHONE_DISPLAY}
                </TextUsLink>
              )}
              <button
                type="button"
                onClick={() => {
                  logStatsigEvent('plumber_book_cta_clicked', plumber._id, { placement: 'cta-card', page: 'directory-profile' })
                  goToEmergencyForm(navigate)
                }}
                className={SHOW_SUPPORT_PHONE
                  ? 'inline-flex items-center gap-1 mt-3 text-sm font-semibold text-gray-300 hover:text-white transition-colors'
                  : 'flex w-full items-center justify-center gap-2 bg-emergency-600 hover:bg-emergency-700 text-white font-bold px-6 py-3.5 rounded-xl transition-colors text-sm'}
              >
                {SHOW_SUPPORT_PHONE ? 'Or get emergency help online' : 'Get emergency help now'} <ChevronRight className="h-4 w-4" />
              </button>
              <p className="text-xs text-gray-500 mt-3">We'll find and confirm the fastest available plumber near you.</p>
              {/* Our number is back on profile pages (issue #78) now that the
                  line actually does something: it takes a voicemail and
                  answers texts (issue #79). 404a96d removed the old version
                  for a reason that voicemail does NOT fix though - it sat
                  under the plumber's name and above "Book online", so people
                  rang us thinking they were ringing them. What fixes that is
                  saying whose number it is and not putting it in the
                  plumber's own contact slot. So: inside this block, which is
                  already unmistakably about FixDelivered's service, named
                  explicitly, and secondary to the button above it. The mobile
                  sticky bar stays suppressed via hideCallBar - pinned across
                  a specific plumber's profile was the worst version. */}
            </div>

            </div>
          </div>

          {/* ── Sticky Sidebar ────────────────────────────────────────────────── */}
          <div className={`${DIRECTORY_SIDEBAR_COL} lg:sticky lg:top-[76px] lg:self-start`}>
          <div className="space-y-4">

            {/* Call / Emergency CTA - see the dark CTA block above for why the
                call action is back as the primary one here (issue #78). */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 space-y-2.5">
              {SHOW_SUPPORT_PHONE && (
                <TextUsLink
                  onClick={() => logStatsigEvent('plumber_text_cta_clicked', plumber._id, { placement: 'sidebar', page: 'directory-profile' })}
                  className="flex items-center justify-center gap-2 w-full bg-emergency-500 hover:bg-emergency-600 text-white font-bold px-4 py-3.5 rounded-xl transition-colors text-sm"
                >
                  <Phone className="h-4 w-4" />
                  Text us now · {SUPPORT_PHONE_DISPLAY}
                </TextUsLink>
              )}
              <button
                type="button"
                onClick={() => {
                  logStatsigEvent('plumber_book_cta_clicked', plumber._id, { placement: 'sidebar', page: 'directory-profile' })
                  goToEmergencyForm(navigate)
                }}
                className={SHOW_SUPPORT_PHONE
                  ? 'flex items-center justify-center gap-2 w-full bg-white border border-gray-200 hover:border-trust-400 text-gray-700 font-semibold px-4 py-3 rounded-xl transition-colors text-sm'
                  : 'flex items-center justify-center gap-2 w-full bg-emergency-500 hover:bg-emergency-600 text-white font-bold px-4 py-3.5 rounded-xl transition-colors text-sm'}
              >
                Get help now
              </button>
              <p className="text-xs text-gray-400 text-center">
                We'll find and confirm the fastest available plumber near you.
              </p>
            </div>

            {/* Quick facts */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-3">
              {googleRating !== undefined && googleRating > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Rating</span>
                  <div className="flex items-center gap-1">
                    <Star className="h-4 w-4 text-amber-400 fill-amber-400" />
                    <span className="text-sm font-semibold text-gray-800">{googleRating.toFixed(1)}</span>
                    {googleReviewCount !== undefined && <span className="text-xs text-gray-400">({googleReviewCount})</span>}
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Emergency</span>
                <span className={`text-sm font-semibold ${plumber.emergencyAvailable ? 'text-emerald-600' : 'text-gray-400'}`}>
                  {plumber.emergencyAvailable ? '24/7 available' : 'Not listed'}
                </span>
              </div>
              {plumber.responseTimeHint && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Response time</span>
                  <span className="text-sm font-semibold text-gray-800">
                    {{ '30-min': '30-min call-out', '1-hour': '1-hour call-out', '24-7': '24/7', 'same-day': 'Same-day', 'next-day': 'Next-day' }[plumber.responseTimeHint] ?? plumber.responseTimeHint}
                  </span>
                </div>
              )}
              {plumber.foundedYear && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Established</span>
                  <span className="text-sm font-semibold text-gray-800">{plumber.foundedYear}</span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">From</span>
                <span className="text-sm font-semibold text-gray-800 text-right">{baseLocation || '-'}</span>
              </div>
            </div>

            {plumber.pricing && (plumber.pricing.callOutFeeGbp !== undefined || plumber.pricing.hourlyRateGbp !== undefined || plumber.pricing.noCallOutCharge) && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                  <BadgePoundSterling className="h-3.5 w-3.5" /> Pricing
                </p>
                <div className="space-y-2.5">
                  {plumber.pricing.noCallOutCharge && (
                    <div className="flex items-center gap-2 text-sm text-emerald-700">
                      <Shield className="h-4 w-4 flex-shrink-0" />
                      <span className="font-medium">No call-out charge</span>
                    </div>
                  )}
                  {plumber.pricing.callOutFeeGbp !== undefined && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Call-out fee</span>
                      <span className="text-sm font-semibold text-gray-800">£{plumber.pricing.callOutFeeGbp}</span>
                    </div>
                  )}
                  {plumber.pricing.hourlyRateGbp !== undefined && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Hourly rate</span>
                      <span className="text-sm font-semibold text-gray-800">£{plumber.pricing.hourlyRateGbp}/hr</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {(certifications.length > 0 || qualifications.length > 0) && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Certifications</p>
                <div className="space-y-2">
                  {[...certifications, ...qualifications].map(c => (
                    <CertBadge
                      key={c}
                      name={c}
                      suffix={c === 'Gas Safe Registered' && plumber.gasSafeNumber ? `#${plumber.gasSafeNumber}` : undefined}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Areas covered */}
            {(plumber.coverageDistricts?.length ?? 0) > 0 && (() => {
              const covMap = new Map((plumber.coverageLinks ?? []).map(c => [c.district.toUpperCase(), c.url]))
              const all = [...(plumber.coverageDistricts ?? [])].sort()
              const SHOW = 12
              const visible = showAllCoverage ? all : all.slice(0, SHOW)
              const hidden = all.length - SHOW
              return (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                    <Navigation className="h-3.5 w-3.5" /> Postcodes covered
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {visible.map(d => {
                      const url = covMap.get(d.toUpperCase())
                      const cls = 'inline-block text-xs font-medium bg-gray-50 text-gray-700 border border-gray-200 px-2 py-0.5 rounded-md uppercase tracking-wide'
                      return url
                        ? <Link key={d} to={url} className={`${cls} hover:border-trust-300 hover:text-trust-700 transition-colors`}>{d}</Link>
                        : <span key={d} className={cls}>{d}</span>
                    })}
                  </div>
                  {all.length > SHOW && (
                    <button
                      type="button"
                      onClick={() => setShowAllCoverage(v => !v)}
                      className="mt-2.5 flex items-center gap-1 text-xs text-trust-500 hover:text-trust-700 transition-colors"
                    >
                      {showAllCoverage
                        ? <><ChevronUp className="h-3.5 w-3.5" /> Show fewer</>
                        : <><ChevronDown className="h-3.5 w-3.5" /> +{hidden} more</>}
                    </button>
                  )}
                </div>
              )
            })()}

          </div>{/* space-y-4 */}
          </div>{/* DIRECTORY_SIDEBAR_COL */}
        </div>

        {townSlug && countySlug && countrySlug && (
          <OtherPlumbers
            basePath={basePath}
            countrySlug={countrySlug}
            countySlug={countySlug}
            townSlug={townSlug}
            townLabel={townLabel || (townSlug ? slugToLabel(townSlug) : '')}
            excludeSlug={directorySlug}
          />
        )}
      </div>
    </PageWrapper>
  )
}
